import React, { useEffect, useState } from 'react';

import { WindowLocation } from '@reach/router';
import { GlobalBodyStyles, GlobalBodyStylesProps, StyledMain } from './GlobalMain.styled';
import { MainLayout } from 'molecules/Header/Header/Header';
import { TypographyGlobalVariables } from 'styles/typography';
import { ColorPrimitiveGlobalVariables, ShadowGlobalVariables } from 'styles/colors';
import { ColorModeGlobalVariables, GradientGlobalVariables } from 'styles/colors';
import { SpacingGlobalVariables } from 'styles/spacing';
import { ExpandedMenuControllerProvider } from 'src/hooks/useExpandedMenuController';

import 'styles/fonts.scss';
import Footer from 'molecules/Footer/Footer';

import StandardEmailPopup from 'molecules/EmailPopup/Standard/StandardEmailPopup';
import ProEmailPopup from 'molecules/EmailPopup/Pro/ProEmailPopup';
import { EmailPopupCustomization } from 'src/hooks/usePopupVisibility';
import Stack from 'atoms/Layout/Stack/Stack';
import HeaderAndInfoBarWrapper from '../HeaderAndInfoBarWrapper/HeaderAndInfoBarWrapper';
import getQueryParams from 'src/functions/getQueryParams';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';

interface GlobalMainTheme extends GlobalBodyStylesProps {
  hidePopup?: boolean;
}

interface GlobalMainProps<LocationState = WindowLocation['state']> {
  children: React.ReactNode;
  location?: WindowLocation<LocationState>;
  mainLayout?: MainLayout;
  mainTheme?: GlobalMainTheme;
  topbar?: boolean | (() => JSX.Element);
  footer?: boolean | (() => JSX.Element);
  popupCustomization?: EmailPopupCustomization;
}

const GlobalMain = ({
  children,
  location,
  mainLayout = 'standard',
  mainTheme = {},
  topbar = true,
  footer = true,
  popupCustomization,
}: GlobalMainProps): JSX.Element => {
  const [inAppView, setInAppView] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const queryParams = getQueryParams();
      const inAppView = queryParams.hide_chrome === '1';

      if (inAppView) {
        setSessionStorage('hide_chrome', '1');
        setInAppView(true);
      } else {
        const inAppViewCache = getSessionStorage('hide_chrome') === '1';
        setInAppView(inAppViewCache);
      }
    }
  }, []);

  const renderPopup = () => {
    if (inAppView || mainTheme.hidePopup) return null;

    if (mainLayout === 'pro') {
      return <ProEmailPopup customization={popupCustomization} />;
    }

    return <StandardEmailPopup customization={popupCustomization} />;
  };

  const renderFooter = () => {
    if (inAppView || !footer) return null;
    if (typeof footer === 'function') return footer();

    return <Footer type={mainLayout} />;
  };

  const renderHeader = () => {
    if (inAppView || !topbar) return null;
    if (typeof topbar === 'function') return topbar();

    return (
      <ExpandedMenuControllerProvider>
        <HeaderAndInfoBarWrapper
          headerProps={{
            locationProps: location,
            mainLayout: mainLayout,
          }}
        />
      </ExpandedMenuControllerProvider>
    );
  };

  return (
    <StyledMain id='global-main'>
      <SpacingGlobalVariables />
      <ColorPrimitiveGlobalVariables />
      <GradientGlobalVariables />
      <ColorModeGlobalVariables />
      <ShadowGlobalVariables />
      <TypographyGlobalVariables />
      <GlobalBodyStyles backgroundColor={mainTheme.backgroundColor} />
      <Stack direction='column'>
        {renderPopup()}
        {renderHeader()}
        {children}
        {renderFooter()}
      </Stack>
    </StyledMain>
  );
};

export default GlobalMain;
